/**
 * Narrow suppressor for Google Auto Ads vignette / interstitial / anchor overlays.
 * Does not remove in-flow `ins.adsbygoogle` units that sit in document flow.
 */

const MUI_SKIP_SELECTOR =
  '.MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiDialog-root, .MuiDrawer-root, .MuiPopper-root';

function isMuiRelated(el: Element): boolean {
  const className = el.className?.toString() || '';
  if (className.includes('Mui') || className.includes('mui-')) {
    return true;
  }
  return Boolean(el.closest(MUI_SKIP_SELECTOR));
}

function looksLikeGoogleOverlay(el: Element): boolean {
  if (isMuiRelated(el)) {
    return false;
  }
  const id = (el.id || '').toLowerCase();
  const className = (el.className?.toString() || '').toLowerCase();
  const tag = el.tagName?.toLowerCase() || '';

  // In-flow units: leave alone
  if (tag === 'ins' && className.includes('adsbygoogle')) {
    const style = window.getComputedStyle(el);
    if (style.position === 'static' || style.position === 'relative') {
      return false;
    }
  }

  const isAutoPlacedOverlay =
    className.includes('google-auto-placed') ||
    className.includes('google-vignette') ||
    className.includes('adsbygoogle-noablate') ||
    id.includes('google_ads_iframe') && className.includes('overlay');

  if (isAutoPlacedOverlay) {
    return true;
  }

  if (tag === 'div' || tag === 'iframe') {
    try {
      const style = window.getComputedStyle(el);
      const z = parseInt(style.zIndex || '0', 10);
      const fixed = style.position === 'fixed';
      const covers =
        (style.inset === '0px' ||
          (style.top === '0px' && style.left === '0px' && style.width === '100%' && style.height === '100%')) ||
        (parseFloat(style.width) >= window.innerWidth * 0.9 &&
          parseFloat(style.height) >= window.innerHeight * 0.5);
      const adish =
        className.includes('adsbygoogle') ||
        className.includes('google-auto') ||
        id.includes('aswift') ||
        id.includes('google_ads') ||
        (tag === 'iframe' &&
          ((el as HTMLIFrameElement).src || '').includes('googlesyndication'));
      // Anchor bars: fixed bottom/top full width
      const isAnchor =
        fixed &&
        (style.bottom === '0px' || style.top === '0px') &&
        parseFloat(style.width) >= window.innerWidth * 0.8 &&
        adish;
      if (fixed && z >= 1000 && adish && (covers || isAnchor)) {
        return true;
      }
    } catch {
      return false;
    }
  }

  return false;
}

/** One-shot pass: remove known Google overlay nodes. */
export function suppressGoogleOverlayAdsOnce(): number {
  if (typeof document === 'undefined') {
    return 0;
  }
  let removed = 0;
  const candidates = document.querySelectorAll(
    '[class*="google-auto-placed"], [class*="google-vignette"], [id*="google_ads"], [id*="aswift_"], iframe[src*="googlesyndication"], div[style*="position: fixed"], div[style*="position:fixed"]',
  );
  candidates.forEach((el) => {
    if (looksLikeGoogleOverlay(el)) {
      el.remove();
      removed++;
    }
  });
  return removed;
}

/**
 * Observe for Google overlay injections. Returns disconnect cleanup.
 */
export function startGoogleOverlayAdsSuppressor(): () => void {
  if (typeof document === 'undefined' || !document.body) {
    return () => undefined;
  }

  suppressGoogleOverlayAdsOnce();

  const observer = new MutationObserver(() => {
    suppressGoogleOverlayAdsOnce();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  const interval = window.setInterval(() => {
    suppressGoogleOverlayAdsOnce();
  }, 2000);

  return () => {
    observer.disconnect();
    window.clearInterval(interval);
  };
}
